import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Plus, Search as SearchIcon, ChevronDown, Play, Zap, Globe, Bell, Terminal, GitBranch, Database, Check, AlertCircle, X, Clock } from 'lucide-react';
import { EmptyState } from './EmptyState';
type StepType = 'trigger' | 'search' | 'api' | 'notify' | 'termo' | 'branch';
interface OutputField {
  key: string;
  value: string;
  type?: string;
}
interface ConfigField {
  key: string;
  label: string;
  type: 'text' | 'select' | 'textarea' | 'toggle';
  value: string;
  options?: string[];
  required?: boolean;
  tokens?: {
    step: number;
    field: string;
  }[];
}
interface Step {
  id: string;
  type: StepType;
  num: number;
  app: string;
  action: string;
  status: 'configured' | 'needs-config' | 'tested';
  fields: ConfigField[];
  outputs: OutputField[];
}
interface RunStepResult {
  stepNum: number;
  app: string;
  status: 'success' | 'failed' | 'skipped';
  duration: string;
  outputs: OutputField[];
}
interface WorkflowRun {
  id: string;
  time: string;
  status: 'success' | 'failed' | 'running';
  duration: string;
  triggerData: string;
  steps: RunStepResult[];
}
interface Workflow {
  id: string;
  name: string;
  active: boolean;
  lastRun?: string;
  triggerApp: string;
  triggerAction: string;
  triggerConfig: string;
  triggerOutputs: OutputField[];
  steps: Step[];
  history: WorkflowRun[];
}
const STEP_META: Record<StepType, {
  icon: React.FC<{
    size?: number;
    className?: string;
  }>;
  colour: string;
}> = {
  trigger: {
    icon: Zap,
    colour: '#4a9a9a'
  },
  search: {
    icon: Database,
    colour: '#7a8a9a'
  },
  api: {
    icon: Globe,
    colour: '#9a7a4a'
  },
  notify: {
    icon: Bell,
    colour: '#6a9a6a'
  },
  termo: {
    icon: Terminal,
    colour: '#9a6a9a'
  },
  branch: {
    icon: GitBranch,
    colour: '#e07a5f'
  }
};
const SAMPLE_HISTORY: WorkflowRun[] = [{
  id: 'r1',
  time: 'today 9:02 AM',
  status: 'success',
  duration: '4.2s',
  triggerData: 'INV-2026-042 Hetzner $47.20',
  steps: [{
    stepNum: 2,
    app: 'Zenbot Search',
    status: 'success',
    duration: '0.8s',
    outputs: [{
      key: 'match_count',
      value: '3'
    }, {
      key: 'top_match.category',
      value: 'Infrastructure'
    }]
  }, {
    stepNum: 3,
    app: 'Termo',
    status: 'success',
    duration: '2.1s',
    outputs: [{
      key: 'category',
      value: 'Infrastructure'
    }, {
      key: 'confidence',
      value: '0.94'
    }]
  }, {
    stepNum: 4,
    app: 'Notify',
    status: 'skipped',
    duration: '0.1s',
    outputs: []
  }]
}, {
  id: 'r2',
  time: 'yesterday 3:15 PM',
  status: 'success',
  duration: '3.8s',
  triggerData: 'INV-2026-041 Vercel $20.00',
  steps: [{
    stepNum: 2,
    app: 'Zenbot Search',
    status: 'success',
    duration: '0.6s',
    outputs: [{
      key: 'match_count',
      value: '2'
    }, {
      key: 'top_match.category',
      value: 'Infrastructure'
    }]
  }, {
    stepNum: 3,
    app: 'Termo',
    status: 'success',
    duration: '1.9s',
    outputs: [{
      key: 'category',
      value: 'Infrastructure'
    }, {
      key: 'confidence',
      value: '0.91'
    }]
  }, {
    stepNum: 4,
    app: 'Notify',
    status: 'skipped',
    duration: '0.1s',
    outputs: []
  }]
}, {
  id: 'r3',
  time: 'Tuesday 11:30 AM',
  status: 'failed',
  duration: '1.2s',
  triggerData: 'INV-2026-040 Unknown Vendor $890.00',
  steps: [{
    stepNum: 2,
    app: 'Zenbot Search',
    status: 'success',
    duration: '0.7s',
    outputs: [{
      key: 'match_count',
      value: '0'
    }]
  }, {
    stepNum: 3,
    app: 'Termo',
    status: 'failed',
    duration: '0.4s',
    outputs: [{
      key: 'error',
      value: 'No vendor match found. Cannot categorise.'
    }]
  }, {
    stepNum: 4,
    app: 'Notify',
    status: 'skipped',
    duration: '0s',
    outputs: []
  }]
}, {
  id: 'r4',
  time: 'Monday 9:01 AM',
  status: 'success',
  duration: '5.1s',
  triggerData: 'INV-2026-039 OpenAI $156.80',
  steps: [{
    stepNum: 2,
    app: 'Zenbot Search',
    status: 'success',
    duration: '0.9s',
    outputs: [{
      key: 'match_count',
      value: '5'
    }, {
      key: 'top_match.category',
      value: 'AI / APIs'
    }]
  }, {
    stepNum: 3,
    app: 'Termo',
    status: 'success',
    duration: '2.4s',
    outputs: [{
      key: 'category',
      value: 'AI / APIs'
    }, {
      key: 'confidence',
      value: '0.98'
    }]
  }, {
    stepNum: 4,
    app: 'Notify',
    status: 'success',
    duration: '0.3s',
    outputs: [{
      key: 'sent',
      value: 'true'
    }]
  }]
}, {
  id: 'r5',
  time: 'last Friday 2:20 PM',
  status: 'success',
  duration: '3.5s',
  triggerData: 'INV-2026-038 LiveKit $75.00',
  steps: [{
    stepNum: 2,
    app: 'Zenbot Search',
    status: 'success',
    duration: '0.5s',
    outputs: [{
      key: 'match_count',
      value: '1'
    }]
  }, {
    stepNum: 3,
    app: 'Termo',
    status: 'success',
    duration: '2.2s',
    outputs: [{
      key: 'category',
      value: 'AI / APIs'
    }]
  }, {
    stepNum: 4,
    app: 'Notify',
    status: 'success',
    duration: '0.3s',
    outputs: [{
      key: 'sent',
      value: 'true'
    }]
  }]
}];
const WORKFLOWS: Workflow[] = [{
  id: 'w1',
  name: 'New invoice processing',
  active: true,
  lastRun: 'this morning',
  triggerApp: 'QuickBooks',
  triggerAction: 'New Invoice',
  triggerConfig: 'Any new invoice created',
  triggerOutputs: [{
    key: 'invoice_id',
    value: 'INV-2026-042',
    type: 'string'
  }, {
    key: 'vendor_name',
    value: 'Hetzner Online GmbH',
    type: 'string'
  }, {
    key: 'amount',
    value: '47.20',
    type: 'number'
  }, {
    key: 'currency',
    value: 'GBP',
    type: 'string'
  }, {
    key: 'category',
    value: '',
    type: 'string'
  }, {
    key: 'date',
    value: '2026-04-11',
    type: 'date'
  }],
  history: SAMPLE_HISTORY,
  steps: [{
    id: 's1',
    type: 'search',
    num: 2,
    app: 'Zenbot Search',
    action: 'Vector Search',
    status: 'tested',
    fields: [{
      key: 'query',
      label: 'Search query',
      type: 'text',
      value: '',
      required: true,
      tokens: [{
        step: 1,
        field: 'vendor_name'
      }]
    }, {
      key: 'source',
      label: 'Source',
      type: 'select',
      value: 'Vector',
      options: ['Vector', 'Convex', 'Both']
    }, {
      key: 'limit',
      label: 'Max results',
      type: 'text',
      value: '5'
    }],
    outputs: [{
      key: 'match_count',
      value: '3',
      type: 'number'
    }, {
      key: 'top_match.name',
      value: 'Hetzner Online GmbH',
      type: 'string'
    }, {
      key: 'top_match.category',
      value: 'Infrastructure',
      type: 'string'
    }, {
      key: 'top_match.avg_amount',
      value: '45.80',
      type: 'number'
    }]
  }, {
    id: 's2',
    type: 'termo',
    num: 3,
    app: 'Termo',
    action: 'Run Agent',
    status: 'tested',
    fields: [{
      key: 'agent',
      label: 'Agent / Skill',
      type: 'select',
      value: 'Finance Agent',
      options: ['Finance Agent', 'Legal Agent', 'Zen Bot Project', 'Claude Code', 'Whisper']
    }, {
      key: 'instruction',
      label: 'Instruction',
      type: 'textarea',
      value: '',
      tokens: [{
        step: 1,
        field: 'vendor_name'
      }, {
        step: 1,
        field: 'amount'
      }, {
        step: 2,
        field: 'top_match.category'
      }]
    }, {
      key: 'timeout',
      label: 'Timeout (seconds)',
      type: 'text',
      value: '30'
    }],
    outputs: [{
      key: 'category',
      value: 'Infrastructure',
      type: 'string'
    }, {
      key: 'confidence',
      value: '0.94',
      type: 'number'
    }, {
      key: 'note',
      value: 'Matches historical Hetzner invoices. Monthly hosting.',
      type: 'string'
    }]
  }, {
    id: 's3',
    type: 'notify',
    num: 4,
    app: 'Notify',
    action: 'Send Message',
    status: 'configured',
    fields: [{
      key: 'channel',
      label: 'Channel',
      type: 'select',
      value: 'Slack',
      options: ['Slack', 'SMS', 'Email', 'Push notification']
    }, {
      key: 'target',
      label: 'Target',
      type: 'text',
      value: '#finance'
    }, {
      key: 'message',
      label: 'Message',
      type: 'textarea',
      value: '',
      tokens: [{
        step: 1,
        field: 'vendor_name'
      }, {
        step: 1,
        field: 'amount'
      }, {
        step: 3,
        field: 'category'
      }]
    }, {
      key: 'condition',
      label: 'Only if',
      type: 'text',
      value: '',
      tokens: [{
        step: 1,
        field: 'amount'
      }]
    }],
    outputs: []
  }]
}, {
  id: 'w2',
  name: 'Daily standup prep',
  active: true,
  lastRun: 'this morning',
  triggerApp: 'Schedule',
  triggerAction: 'Recurring',
  triggerConfig: 'Every weekday 8:50 AM',
  triggerOutputs: [{
    key: 'timestamp',
    value: '2026-04-11T08:50:00Z',
    type: 'date'
  }],
  history: [],
  steps: [{
    id: 's1',
    type: 'search',
    num: 2,
    app: 'Zenbot Search',
    action: 'Query Database',
    status: 'tested',
    fields: [{
      key: 'query',
      label: 'Query',
      type: 'text',
      value: 'Jira issues updated since yesterday',
      required: true
    }],
    outputs: [{
      key: 'count',
      value: '3',
      type: 'number'
    }]
  }, {
    id: 's2',
    type: 'termo',
    num: 3,
    app: 'Termo',
    action: 'Run Agent',
    status: 'tested',
    fields: [{
      key: 'agent',
      label: 'Agent',
      type: 'select',
      value: 'Zen Bot Project',
      options: ['Finance Agent', 'Legal Agent', 'Zen Bot Project']
    }, {
      key: 'instruction',
      label: 'Instruction',
      type: 'textarea',
      value: 'Summarise blockers'
    }],
    outputs: [{
      key: 'summary',
      value: 'Two blockers...',
      type: 'string'
    }]
  }, {
    id: 's3',
    type: 'notify',
    num: 4,
    app: 'Notify',
    action: 'Push Notification',
    status: 'configured',
    fields: [{
      key: 'channel',
      label: 'Channel',
      type: 'select',
      value: 'Push notification',
      options: ['Slack', 'SMS', 'Email', 'Push notification']
    }],
    outputs: []
  }]
}, {
  id: 'w3',
  name: 'Contract review',
  active: true,
  lastRun: 'yesterday',
  triggerApp: 'Google Drive',
  triggerAction: 'New File',
  triggerConfig: 'New file in /Contracts',
  triggerOutputs: [{
    key: 'file_name',
    value: 'Acme_v3.pdf',
    type: 'string'
  }],
  history: [],
  steps: [{
    id: 's1',
    type: 'search',
    num: 2,
    app: 'Zenbot Search',
    action: 'Vector Search',
    status: 'tested',
    fields: [{
      key: 'query',
      label: 'Query',
      type: 'text',
      value: 'Similar contracts',
      required: true
    }],
    outputs: [{
      key: 'match_count',
      value: '2'
    }]
  }, {
    id: 's2',
    type: 'termo',
    num: 3,
    app: 'Termo',
    action: 'Run Agent',
    status: 'tested',
    fields: [{
      key: 'agent',
      label: 'Agent',
      type: 'select',
      value: 'Legal Agent',
      options: ['Legal Agent']
    }],
    outputs: [{
      key: 'risk_level',
      value: 'medium'
    }]
  }, {
    id: 's3',
    type: 'branch',
    num: 4,
    app: 'Branch',
    action: 'Route by Condition',
    status: 'configured',
    fields: [{
      key: 'condition',
      label: 'Condition',
      type: 'text',
      value: '',
      tokens: [{
        step: 3,
        field: 'risk_level'
      }]
    }, {
      key: 'if_high',
      label: 'If high',
      type: 'text',
      value: 'Escalation workflow'
    }, {
      key: 'if_low',
      label: 'If low',
      type: 'text',
      value: 'Auto-approve workflow'
    }],
    outputs: []
  }]
}, {
  id: 'w4',
  name: 'Weekly spend report',
  active: false,
  triggerApp: 'Schedule',
  triggerAction: 'Recurring',
  triggerConfig: 'Every Monday 9:00 AM',
  triggerOutputs: [],
  history: [],
  steps: [{
    id: 's1',
    type: 'api',
    num: 2,
    app: 'REST API',
    action: 'GET Request',
    status: 'configured',
    fields: [{
      key: 'method',
      label: 'Method',
      type: 'select',
      value: 'GET',
      options: ['GET', 'POST', 'PUT', 'DELETE']
    }, {
      key: 'url',
      label: 'URL',
      type: 'text',
      value: 'https://api.quickbooks.com/v3/transactions',
      required: true
    }],
    outputs: []
  }, {
    id: 's2',
    type: 'termo',
    num: 3,
    app: 'Termo',
    action: 'Run Agent',
    status: 'needs-config',
    fields: [{
      key: 'agent',
      label: 'Agent',
      type: 'select',
      value: 'Finance Agent',
      options: ['Finance Agent']
    }],
    outputs: []
  }, {
    id: 's3',
    type: 'notify',
    num: 4,
    app: 'Notify',
    action: 'Send Message',
    status: 'needs-config',
    fields: [{
      key: 'channel',
      label: 'Channel',
      type: 'select',
      value: '',
      options: ['Slack', 'SMS', 'Email']
    }],
    outputs: []
  }]
}];
const STEP_OPTIONS: {
  type: StepType;
  app: string;
  description: string;
}[] = [{
  type: 'search',
  app: 'Zenbot Search',
  description: 'Vector or database query'
}, {
  type: 'api',
  app: 'REST API',
  description: 'HTTP request to any endpoint'
}, {
  type: 'notify',
  app: 'Notify',
  description: 'Slack, SMS, email, or push'
}, {
  type: 'termo',
  app: 'Termo',
  description: 'Agent, CLI tool, or OpenClaw skill'
}, {
  type: 'branch',
  app: 'Branch',
  description: 'Route to another workflow'
}];
export function WorkflowsView() {
  const [search, setSearch] = useState('');
  const [viewingId, setViewingId] = useState<string | null>(null);
  const [creatingNew, setCreatingNew] = useState(false);
  if (viewingId) {
    const wf = WORKFLOWS.find(w => w.id === viewingId);
    if (wf) return <WorkflowEditor workflow={wf} onBack={() => setViewingId(null)} data-id="element-2342" />;
  }
  if (creatingNew) return <WorkflowEditor workflow={null} onBack={() => setCreatingNew(false)} data-id="element-2343" />;
  const filtered = WORKFLOWS.filter(w => !search || w.name.toLowerCase().includes(search.toLowerCase()));
  return <div className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-2344">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-2345">
        <h1 className="text-[20px] font-medium text-[#e8e4df] tracking-tight mb-6" data-id="element-2346">Workflows</h1>
        <div className="relative mb-8" data-id="element-2347"><SearchIcon size={14} className="absolute left-0 top-1/2 -translate-y-1/2 text-[#8a8680]/40" data-id="element-2348" /><input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search workflows" className="w-full bg-transparent text-[#e8e4df] pl-6 pr-4 py-2 text-[14px] outline-none placeholder:text-[#8a8680]/30 border-b border-[#8a8680]/10 focus:border-[#8a8680]/30 transition-colors" data-id="element-2349" /></div>
        <div className="flex flex-col gap-3" data-id="element-2350">{filtered.map((wf, i) => {
          const needsConfig = wf.steps.some(s => s.status === 'needs-config');
          return <motion.button key={wf.id} initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            duration: 0.3,
            delay: i * 0.04
          }} onClick={() => setViewingId(wf.id)} className="bg-[#1c1c22] rounded-[16px] p-5 text-left group hover:bg-[#1e1e25] transition-colors w-full" data-id="element-2351">
            <div className="flex items-center gap-4" data-id="element-2352"><div className="w-10 h-10 rounded-[12px] bg-[#252530] flex items-center justify-center shrink-0" data-id="element-2353"><Zap size={16} className={wf.active ? 'text-[#4a9a9a]' : 'text-[#8a8680]/40'} strokeWidth={1.5} data-id="element-2354" /></div><div className="flex-1 min-w-0" data-id="element-2355"><span className={`text-[15px] ${wf.active ? 'text-[#e8e4df]' : 'text-[#e8e4df]/50'}`} data-id="element-2356">{wf.name}</span><div className="flex items-center gap-3 mt-1" data-id="element-2357"><span className="text-[11px] text-[#8a8680]/40" data-id="element-2358">{wf.triggerApp}</span><span className="text-[11px] text-[#8a8680]/40" data-id="element-2359">{wf.steps.length} steps</span>{needsConfig && <span className="text-[11px] text-amber-400/50" data-id="element-2360">needs config</span>}{wf.lastRun && <span className="text-[11px] text-[#8a8680]/30" data-id="element-2361">{wf.lastRun}</span>}</div></div>{wf.active && <div className="w-1.5 h-1.5 rounded-full bg-[#4a9a9a] opacity-70 shrink-0" data-id="element-2362" />}</div>
          </motion.button>;
        })}{filtered.length === 0 && <EmptyState message="No workflows." data-id="element-2363" />}</div>
        <button onClick={() => setCreatingNew(true)} className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors text-[13px] mt-8" data-id="element-2364"><Plus size={13} data-id="element-2365" />New workflow</button>
      </div>
    </div>;
}
function TokenPill({
  step,
  field
}: {
  step: number;
  field: string;
}) {
  return <span className="inline-flex items-center gap-1 bg-[#4a9a9a]/15 text-[#4a9a9a] text-[11px] px-1.5 py-0.5 rounded mx-0.5" data-id="element-2366">{step}.{field}</span>;
}
function WorkflowEditor({
  workflow,
  onBack
}: {
  workflow: Workflow | null;
  onBack: () => void;
}) {
  const isNew = !workflow;
  const [name, setName] = useState(workflow?.name || '');
  const [steps] = useState<Step[]>(workflow?.steps || []);
  const [expandedStep, setExpandedStep] = useState<string | null>(null);
  const [showAddStep, setShowAddStep] = useState(false);
  const [testedSteps, setTestedSteps] = useState<Record<string, boolean>>({});
  const [showOutputs, setShowOutputs] = useState<Record<string, boolean>>({});
  const [activeTab, setActiveTab] = useState<'builder' | 'history'>('builder');
  const [historyFilter, setHistoryFilter] = useState<'all' | 'success' | 'failed'>('all');
  const [expandedRun, setExpandedRun] = useState<string | null>(null);
  const triggerOutputs = workflow?.triggerOutputs || [];
  const hasBranch = steps.some(s => s.type === 'branch');
  const history = workflow?.history || [];
  const filteredHistory = history.filter(r => historyFilter === 'all' || r.status === historyFilter);
  const STATUS_ICONS: Record<string, React.ReactNode> = {
    'configured': <div className="w-1.5 h-1.5 rounded-full bg-[#8a8680]/40" data-id="element-2367" />,
    'needs-config': <AlertCircle size={12} className="text-amber-400/50" data-id="element-2368" />,
    'tested': <Check size={12} className="text-[#4a9a9a]/60" data-id="element-2369" />
  };
  const RUN_STATUS: Record<string, {
    colour: string;
    label: string;
  }> = {
    success: {
      colour: '#4a9a9a',
      label: 'success'
    },
    failed: {
      colour: '#e07a5f',
      label: 'failed'
    },
    running: {
      colour: '#9a7a4a',
      label: 'running'
    }
  };
  return <motion.div initial={{
    opacity: 0,
    x: 20
  }} animate={{
    opacity: 1,
    x: 0
  }} className="min-h-[calc(100vh-56px)] text-[#e8e4df] pt-12 pb-32 px-6" data-id="element-2370">
      <div className="max-w-[640px] mx-auto w-full" data-id="element-2371">
        <button onClick={onBack} className="flex items-center gap-2 text-[#8a8680]/60 hover:text-[#e8e4df] transition-colors -ml-1 mb-8" data-id="element-2372"><ArrowLeft size={14} data-id="element-2373" /><span className="text-[13px]" data-id="element-2374">Workflows</span></button>
        <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="Name this workflow" className="w-full bg-transparent text-[20px] font-medium outline-none text-[#e8e4df] placeholder:text-[#8a8680]/30 mb-6" data-id="element-2375" />

        {/* Tabs */}
        {!isNew && <nav className="flex items-center gap-6 mb-8 border-b border-[#8a8680]/10" data-id="element-2376">
            {(['builder', 'history'] as const).map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[14px] transition-colors relative capitalize ${activeTab === tab ? 'text-[#e8e4df]' : 'text-[#8a8680] hover:text-[#e8e4df]/80'}`} data-id="element-2377">
                {tab}{tab === 'history' && history.length > 0 && <span className="text-[11px] text-[#8a8680]/40 ml-1.5" data-id="element-2378">{history.length}</span>}
                {activeTab === tab && <motion.div layoutId="wfTab" className="absolute bottom-0 left-0 right-0 h-[2px] bg-[#4a9a9a]" initial={false} transition={{
            type: 'spring',
            stiffness: 500,
            damping: 30
          }} data-id="element-2379" />}
              </button>)}
          </nav>}

        {/* History tab */}
        {activeTab === 'history' && <div data-id="element-2380">
            <div className="flex items-center gap-3 mb-6" data-id="element-2381">
              {(['all', 'success', 'failed'] as const).map(f => <button key={f} onClick={() => setHistoryFilter(f)} className={`text-[12px] transition-colors capitalize ${historyFilter === f ? 'text-[#4a9a9a]' : 'text-[#8a8680]/50 hover:text-[#8a8680]'}`} data-id="element-2382">{f}</button>)}
            </div>

            {filteredHistory.length === 0 ? <EmptyState message="No runs yet." data-id="element-2383" /> : <div className="flex flex-col gap-3" data-id="element-2384">
                {filteredHistory.map((run, i) => {
            const meta = RUN_STATUS[run.status];
            const isExpanded = expandedRun === run.id;
            return <motion.div key={run.id} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.3,
              delay: i * 0.03
            }} className="bg-[#1c1c22] rounded-[16px] overflow-hidden" data-id="element-2385">
                      <button onClick={() => setExpandedRun(isExpanded ? null : run.id)} className="w-full flex items-center gap-4 p-5 text-left hover:bg-[#1e1e25] transition-colors" data-id="element-2386">
                        <div className="w-2 h-2 rounded-full shrink-0" style={{
                  backgroundColor: meta.colour,
                  opacity: 0.7
                }} data-id="element-2387" />
                        <div className="flex-1 min-w-0" data-id="element-2388">
                          <span className="text-[14px] text-[#e8e4df]/80" data-id="element-2389">{run.triggerData}</span>
                          <div className="flex items-center gap-3 mt-1" data-id="element-2390">
                            <span className="text-[11px]" style={{
                      color: `${meta.colour}99`
                    }} data-id="element-2391">{meta.label}</span>
                            <span className="text-[11px] text-[#8a8680]/40" data-id="element-2392">{run.duration}</span>
                            <span className="text-[11px] text-[#8a8680]/40" data-id="element-2393">{run.time}</span>
                          </div>
                        </div>
                        <ChevronDown size={12} className={`text-[#8a8680]/30 transition-transform ${isExpanded ? '' : '-rotate-90'}`} data-id="element-2394" />
                      </button>

                      <AnimatePresence data-id="element-2395">
                        {isExpanded && <motion.div initial={{
                  height: 0
                }} animate={{
                  height: 'auto'
                }} exit={{
                  height: 0
                }} className="overflow-hidden" data-id="element-2396">
                            <div className="px-5 pb-5 border-t border-[#8a8680]/10 pt-3" data-id="element-2397">
                              {run.steps.map((rs, si) => {
                      const stepStatus = RUN_STATUS[rs.status] || {
                        colour: '#8a8680',
                        label: rs.status
                      };
                      return <div key={si} className="flex items-start gap-3 py-2" data-id="element-2398">
                                    <div className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{
                          backgroundColor: stepStatus.colour,
                          opacity: 0.6
                        }} data-id="element-2399" />
                                    <div className="flex-1" data-id="element-2400">
                                      <div className="flex items-center gap-2" data-id="element-2401">
                                        <span className="text-[13px] text-[#e8e4df]/70" data-id="element-2402">{rs.stepNum}. {rs.app}</span>
                                        <span className="text-[11px]" style={{
                              color: `${stepStatus.colour}80`
                            }} data-id="element-2403">{stepStatus.label}</span>
                                        <span className="text-[11px] text-[#8a8680]/30" data-id="element-2404">{rs.duration}</span>
                                      </div>
                                      {rs.outputs.length > 0 && <div className="mt-1 pl-1" data-id="element-2405">
                                          {rs.outputs.map(o => <div key={o.key} className="flex items-center gap-2 py-0.5" data-id="element-2406"><span className="text-[10px] text-[#8a8680]/50 font-mono" data-id="element-2407">{o.key}</span><span className="text-[10px] text-[#e8e4df]/50" data-id="element-2408">{o.value}</span></div>)}
                                        </div>}
                                    </div>
                                  </div>;
                    })}
                            </div>
                          </motion.div>}
                      </AnimatePresence>
                    </motion.div>;
          })}
              </div>}
          </div>}

        {/* Builder tab */}
        {(activeTab === 'builder' || isNew) && <>
            {/* Trigger */}
            <div className="flex flex-col items-center mb-2" data-id="element-2409">
              <div className="bg-[#1c1c22] rounded-[16px] w-full overflow-hidden" data-id="element-2410">
                <div className="p-5" data-id="element-2411">
                  <div className="flex items-center gap-3 mb-3" data-id="element-2412"><div className="w-8 h-8 rounded-full flex items-center justify-center" style={{
                  backgroundColor: '#4a9a9a15'
                }} data-id="element-2413"><Zap size={14} style={{
                    color: '#4a9a9a'
                  }} strokeWidth={1.5} data-id="element-2414" /></div><div className="flex-1" data-id="element-2415"><div className="flex items-center gap-2" data-id="element-2416"><span className="text-[13px] text-[#e8e4df]" data-id="element-2417">1. {workflow?.triggerApp || 'Trigger'}</span><span className="text-[11px] text-[#8a8680]/40" data-id="element-2418">{workflow?.triggerAction || ''}</span></div></div><Check size={12} className="text-[#4a9a9a]/60" data-id="element-2419" /></div>
                  {workflow?.triggerConfig && <p className="text-[12px] text-[#8a8680]/50 ml-11" data-id="element-2420">{workflow.triggerConfig}</p>}
                </div>
                {triggerOutputs.length > 0 && <div className="border-t border-[#8a8680]/10" data-id="element-2421"><button onClick={() => setShowOutputs(prev => ({
                ...prev,
                trigger: !prev.trigger
              }))} className="w-full flex items-center gap-2 px-5 py-2.5 text-left text-[11px] text-[#8a8680]/40 hover:text-[#8a8680] transition-colors" data-id="element-2422"><ChevronDown size={10} className={`transition-transform ${showOutputs.trigger ? '' : '-rotate-90'}`} data-id="element-2423" />Output ({triggerOutputs.length})</button><AnimatePresence data-id="element-2424">{showOutputs.trigger && <motion.div initial={{
                  height: 0
                }} animate={{
                  height: 'auto'
                }} exit={{
                  height: 0
                }} className="overflow-hidden" data-id="element-2425"><div className="px-5 pb-3" data-id="element-2426">{triggerOutputs.map(o => <div key={o.key} className="flex items-center justify-between py-1" data-id="element-2427"><span className="text-[11px] text-[#8a8680]/60 font-mono" data-id="element-2428">1.{o.key}</span><span className="text-[11px] text-[#e8e4df]/60" data-id="element-2429">{o.value}</span></div>)}</div></motion.div>}</AnimatePresence></div>}
              </div>
              <div className="w-px h-6 bg-[#8a8680]/15" data-id="element-2430" />
            </div>

            {/* Steps */}
            {steps.map((step, i) => {
          const meta = STEP_META[step.type];
          const Icon = meta.icon;
          const isExpanded = expandedStep === step.id;
          const isLast = i === steps.length - 1;
          const isTested = testedSteps[step.id] || step.status === 'tested';
          return <div key={step.id} className="flex flex-col items-center mb-2" data-id="element-2431">
                  <div className="bg-[#1c1c22] rounded-[16px] w-full overflow-hidden" data-id="element-2432">
                    <button onClick={() => setExpandedStep(isExpanded ? null : step.id)} className="w-full flex items-center gap-3 p-5 text-left hover:bg-[#1e1e25] transition-colors" data-id="element-2433"><div className="w-8 h-8 rounded-full flex items-center justify-center shrink-0" style={{
                  backgroundColor: `${meta.colour}15`
                }} data-id="element-2434"><Icon size={14} style={{
                    color: meta.colour
                  }} strokeWidth={1.5} data-id="element-2435" /></div><div className="flex-1" data-id="element-2436"><div className="flex items-center gap-2" data-id="element-2437"><span className="text-[13px] text-[#e8e4df]" data-id="element-2438">{step.num}. {step.app}</span><span className="text-[11px] text-[#8a8680]/40" data-id="element-2439">{step.action}</span></div></div>{STATUS_ICONS[step.status]}</button>
                    <AnimatePresence data-id="element-2440">
                      {isExpanded && <motion.div initial={{
                  height: 0
                }} animate={{
                  height: 'auto'
                }} exit={{
                  height: 0
                }} className="overflow-hidden" data-id="element-2441">
                          <div className="px-5 pb-5 border-t border-[#8a8680]/10 pt-4" data-id="element-2442">
                            {step.fields.map(field => <div key={field.key} className="mb-4" data-id="element-2443">
                                <div className="flex items-center gap-2 mb-1.5" data-id="element-2444"><span className="text-[12px] text-[#8a8680]/60" data-id="element-2445">{field.label}</span>{field.required && <span className="text-[10px] text-amber-400/40" data-id="element-2446">required</span>}</div>
                                {field.type === 'select' && field.options ? <div className="flex gap-2 flex-wrap" data-id="element-2447">{field.options.map(o => <button key={o} className={`text-[12px] px-2.5 py-1 rounded-full transition-colors ${field.value === o ? 'text-[#4a9a9a] bg-[#4a9a9a]/10' : 'text-[#8a8680]/50 bg-[#252530]'}`} data-id="element-2448">{o}</button>)}</div> : field.type === 'textarea' ? <div className="bg-[#252530] rounded-md px-3 py-2 min-h-[60px]" data-id="element-2449">{field.tokens && field.tokens.length > 0 ? <div className="flex flex-wrap items-center gap-1" data-id="element-2450">{field.tokens.map((t, ti) => <TokenPill key={ti} step={t.step} field={t.field} data-id="element-2451" />)}{field.value && <span className="text-[13px] text-[#e8e4df]/70" data-id="element-2452">{field.value}</span>}</div> : <span className="text-[13px] text-[#8a8680]/30" data-id="element-2453">{field.value || 'Enter value or map from previous step'}</span>}</div> : <div className="bg-[#252530] rounded-md px-3 py-1.5 flex items-center gap-1 flex-wrap" data-id="element-2454">{field.tokens && field.tokens.length > 0 && field.tokens.map((t, ti) => <TokenPill key={ti} step={t.step} field={t.field} data-id="element-2455" />)}<span className={`text-[13px] ${field.value ? 'text-[#e8e4df]/70' : 'text-[#8a8680]/30'} ${field.key === 'url' ? 'font-mono text-[12px]' : ''}`} data-id="element-2456">{field.value || 'Enter value'}</span></div>}
                              </div>)}
                            <div className="flex items-center gap-4 mt-4 pt-3 border-t border-[#8a8680]/10" data-id="element-2457"><button onClick={() => setTestedSteps(prev => ({
                        ...prev,
                        [step.id]: true
                      }))} className="text-[12px] text-[#4a9a9a]/70 hover:text-[#4a9a9a] flex items-center gap-1.5 transition-colors" data-id="element-2458"><Play size={10} data-id="element-2459" />{isTested ? 'Re-test' : 'Test step'}</button>{isTested && <span className="text-[11px] text-[#4a9a9a]/50 flex items-center gap-1" data-id="element-2460"><Check size={10} data-id="element-2461" />passed</span>}</div>
                          </div>
                          {step.outputs.length > 0 && <div className="border-t border-[#8a8680]/10" data-id="element-2462"><button onClick={() => setShowOutputs(prev => ({
                      ...prev,
                      [step.id]: !prev[step.id]
                    }))} className="w-full flex items-center gap-2 px-5 py-2.5 text-left text-[11px] text-[#8a8680]/40 hover:text-[#8a8680] transition-colors" data-id="element-2463"><ChevronDown size={10} className={`transition-transform ${showOutputs[step.id] ? '' : '-rotate-90'}`} data-id="element-2464" />Output ({step.outputs.length})</button><AnimatePresence data-id="element-2465">{showOutputs[step.id] && <motion.div initial={{
                        height: 0
                      }} animate={{
                        height: 'auto'
                      }} exit={{
                        height: 0
                      }} className="overflow-hidden" data-id="element-2466"><div className="px-5 pb-3" data-id="element-2467">{step.outputs.map(o => <div key={o.key} className="flex items-center justify-between py-1" data-id="element-2468"><span className="text-[11px] text-[#8a8680]/60 font-mono" data-id="element-2469">{step.num}.{o.key}</span><span className="text-[11px] text-[#e8e4df]/60" data-id="element-2470">{o.value}</span></div>)}</div></motion.div>}</AnimatePresence></div>}
                        </motion.div>}
                    </AnimatePresence>
                  </div>
                  {!isLast && <div className="w-px h-6 bg-[#8a8680]/15" data-id="element-2471" />}
                </div>;
        })}

            <div className="flex flex-col items-center mt-2" data-id="element-2472">
              {steps.length > 0 && <div className="w-px h-4 bg-[#8a8680]/15 mb-2" data-id="element-2473" />}
              <div className="relative" data-id="element-2474">
                <button onClick={() => setShowAddStep(!showAddStep)} className="flex items-center gap-2 text-[#8a8680]/50 hover:text-[#e8e4df]/80 transition-colors text-[13px]" data-id="element-2475"><Plus size={13} data-id="element-2476" />Add step</button>
                <AnimatePresence data-id="element-2477">{showAddStep && <><div className="fixed inset-0 z-10" onClick={() => setShowAddStep(false)} data-id="element-2478" /><motion.div initial={{
                  opacity: 0,
                  y: -8
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -8
                }} className="absolute left-0 bottom-full mb-2 rounded-[12px] overflow-hidden z-20 w-[260px] bg-[#1c1c22]" data-id="element-2479"><div className="py-2" data-id="element-2480">{STEP_OPTIONS.filter(o => o.type !== 'branch' || !hasBranch).map(opt => {
                      const m = STEP_META[opt.type];
                      const I = m.icon;
                      return <button key={opt.type} onClick={() => setShowAddStep(false)} className="w-full flex items-center gap-3 px-4 py-2.5 text-left hover:bg-[#252530]/70 transition-colors" data-id="element-2481"><I size={14} style={{
                          color: m.colour
                        }} strokeWidth={1.5} data-id="element-2482" /><div data-id="element-2483"><span className="text-[13px] text-[#e8e4df] block" data-id="element-2484">{opt.app}</span><span className="text-[11px] text-[#8a8680]/50" data-id="element-2485">{opt.description}</span></div></button>;
                    })}</div></motion.div></>}</AnimatePresence>
              </div>
            </div>

            <div className="mt-10 flex items-center gap-6" data-id="element-2486">
              <button className="text-[14px] text-[#4a9a9a]/80 hover:text-[#4a9a9a] transition-colors" data-id="element-2487">{isNew ? 'Create' : 'Save'}</button>
              {!isNew && <button className="text-[14px] text-[#8a8680]/50 hover:text-[#8a8680] transition-colors flex items-center gap-1.5" data-id="element-2488"><Play size={12} data-id="element-2489" />Run now</button>}
            </div>
          </>}
      </div>
    </motion.div>;
}